/**
 * useActivityReconcile
 *
 * Owns the manage/edit "reconcile" flow for an activity: registering newly-selected slots,
 * cancelling de-selected ones, and replacing the guest list on the kept slots in one pass. Cancels
 * run before adds (an add's day-cap/exclusion-group check reads the viewer's other active
 * registrations, so it must see prior cancels as committed) — guest edits target already-kept
 * slots, unaffected by that ordering, so they run concurrently with the cancel phase. The
 * succeeded portion is always reported to the parent (so the card + counts update), then the flow
 * either closes (full success) or reflects reality back into the form (partial failure) via the
 * supplied callbacks.
 *
 * Split out of ActivitySignupModal so the signup form and the reconcile orchestration are
 * separate concerns rather than two flows sharing one 1000-line component.
 */

import { useCallback, useState } from "react";

import type { ActivityRow } from "../_logic/types/db-types";
import type { ViewerIdentity } from "../_logic/actions/_shared/get-invited-page-access";
import { registerForActivityFromSession } from "../_logic/actions/signup-actions";
import { cancelMyActivityRegistrationFromSession } from "../_logic/actions/cancel-registration-actions";
import {
  updateMyActivityGuestsFromSession,
  updateMyActivityGuestsBatchFromSession,
} from "../_logic/actions/manage-registration-guests-actions";
import {
  succeededSlotIdsFrom,
  tallyRegisterResults,
  sendConsolidatedEmailsForBatch,
} from "../_logic/utils/activity-register-results";

interface UseActivityReconcileParams {
  activity: ActivityRow;
  /** Landing page id stamped onto new registration rows. */
  landingPageId: number;
  /** Logged-in viewer identity (null only for anonymous, who never reach this flow). */
  viewer: ViewerIdentity | null;
  phoneDigits: string;
  countryDialCodeId: number | null;
  dietaryRestrictions: string;
  customFieldValues: Record<string, string | boolean>;
  guestCount: number;
  guestNames: string[];
  /** slotId -> registration row id, for the slots the viewer currently holds. */
  registrationBySlotId: Map<number, number>;
  /** This activity's bookable slots the viewer currently holds AND can cancel. */
  registeredSlotIds: Set<number>;
  onSuccess: (params: { activityId: number; addedSlotIds: number[]; cancelledSlotIds: number[] }) => void;
  onClose: () => void;
  showToast: (message: string, type: "success" | "error") => void;
  /** Clear any prior error/partial-failure banners before a run begins. */
  onReconcileStart: () => void;
  /**
   * Called on partial failure to reflect the new reality back into the form: the slot ids the
   * viewer now actually holds, the error to surface, and (when every guest write landed) the new
   * guest baseline so the next confirm diffs against committed state rather than stale state.
   */
  onPartialFailure: (params: { reconciledSlotIds: number[]; errorMessage: string; guestBaseline?: string[] }) => void;
}

interface UseActivityReconcileResult {
  isReconciling: boolean;
  /**
   * Applies a manage diff in one pass: register `addIds`, cancel `cancelIds`, and replace the
   * guest list on the kept registrations in `guestUpdateSlotIds` (omit/empty when guests are
   * unchanged). The current `guestNames`/`guestCount` from the hook params is the new guest set.
   */
  runReconcile: (addIds: number[], cancelIds: number[], guestUpdateSlotIds?: number[]) => Promise<void>;
}

/** Summary of the guest-edit portion of a reconcile pass. */
interface GuestUpdateSummary {
  /** Slots whose guests we attempted to write. */
  total: number;
  /** Of those, how many succeeded. */
  succeeded: number;
  /** Of the successes, how many were pushed to the waitlist because the grown party no longer fit. */
  moved: number;
  /** True when every attempted write succeeded — only then can the form re-baseline to the new list. */
  fullySynced: boolean;
}

/**
 * Pick the success toast for a fully-successful reconcile. Slot changes (add/cancel) take priority
 * over guest-only messaging; the guest-only branch distinguishes "all slots moved to the waitlist"
 * from "some moved" so the copy never overstates what happened.
 *
 * @param params - Succeeded add/cancel counts plus the guest-edit move/update counts.
 * @returns The toast message to show.
 */
function pickReconcileToast(params: {
  addedCount: number;
  cancelledCount: number;
  guestMovedCount: number;
  guestUpdatedCount: number;
}): string {
  const { addedCount, cancelledCount, guestMovedCount, guestUpdatedCount } = params;

  if (cancelledCount > 0 && addedCount === 0) {
    return cancelledCount === 1 ? "Registration cancelled" : "Registrations cancelled";
  }
  if (addedCount > 0 || cancelledCount > 0) {
    return cancelledCount > 0 ? "Your times were updated" : "You're signed up";
  }
  if (guestMovedCount > 0) {
    return guestMovedCount === guestUpdatedCount
      ? "Your party no longer fits, so you've been moved to the waitlist"
      : "Your guests were updated. Some times moved to the waitlist because your party no longer fits.";
  }
  return "Your guests were updated";
}

/**
 * Hook exposing the in-flight flag and the reconcile runner for a manage/edit diff.
 *
 * @param params - Activity + form context and the success/close/partial-failure callbacks.
 * @returns `isReconciling` (true while a diff is applying) and `runReconcile(addIds, cancelIds)`.
 */
export function useActivityReconcile(params: UseActivityReconcileParams): UseActivityReconcileResult {
  const {
    activity,
    landingPageId,
    viewer,
    phoneDigits,
    countryDialCodeId,
    dietaryRestrictions,
    customFieldValues,
    guestCount,
    guestNames,
    registrationBySlotId,
    registeredSlotIds,
    onSuccess,
    onClose,
    showToast,
    onReconcileStart,
    onPartialFailure,
  } = params;

  const [isReconciling, setIsReconciling] = useState(false);

  const runReconcile = useCallback(
    async (addIds: number[], cancelIds: number[], guestUpdateSlotIds: number[] = []) => {
      setIsReconciling(true);
      onReconcileStart();

      const isMultiSlotAdd = addIds.length > 1;
      const phoneOverride = !viewer?.phoneNumber && phoneDigits.trim() ? phoneDigits.trim() : undefined;
      // One explicit guest list for both write paths: adds want `undefined` (no guests) when the
      // party is just the registrant, guest-edits want `[]` (an explicit clear). Deriving both from
      // a single value keeps the two branches from drifting.
      const guestNamesForWrite = activity.allow_plus_one && guestCount > 0 ? guestNames.slice(0, guestCount) : [];
      const addGuestNamesPayload = guestNamesForWrite.length > 0 ? guestNamesForWrite : undefined;

      const cancelPromises = cancelIds.map((slotId) => {
        const regId = registrationBySlotId.get(slotId);
        return regId
          ? cancelMyActivityRegistrationFromSession(regId)
          : Promise.resolve({ success: false, error: "Missing registration" });
      });

      // Guest edits on kept registrations. Multiple kept slots go through one batched action that
      // authenticates once and sends a SINGLE consolidated email per outcome — a viewer holding 3
      // slots gets 1 email, not 3. A single kept slot uses the single-registration action.
      const runGuestUpdates = async (): Promise<GuestUpdateSummary> => {
        const total = guestUpdateSlotIds.length;
        if (total === 0) return { total: 0, succeeded: 0, moved: 0, fullySynced: true };

        const regIds = guestUpdateSlotIds
          .map((slotId) => registrationBySlotId.get(slotId))
          .filter((id): id is number => id != null);
        if (regIds.length === 0) return { total, succeeded: 0, moved: 0, fullySynced: false };

        if (regIds.length === 1) {
          const res = await updateMyActivityGuestsFromSession({
            registrationId: regIds[0]!,
            guestNames: guestNamesForWrite,
          });
          const succeeded = res.success ? 1 : 0;
          const moved = res.success && res.movedToWaitlist ? 1 : 0;
          return { total, succeeded, moved, fullySynced: succeeded === total };
        }

        const res = await updateMyActivityGuestsBatchFromSession({
          registrationIds: regIds,
          guestNames: guestNamesForWrite,
        });
        const succeeded = res.results.filter((r) => r.success).length;
        const moved = res.results.filter((r) => r.success && r.movedToWaitlist).length;
        return { total, succeeded, moved, fullySynced: succeeded === total };
      };

      // Cancels must fully commit before any add starts — an add's day-cap/exclusion-group check
      // (checkSignupLimits) reads the viewer's OTHER active registrations, so a same-pass "switch
      // activities" (cancel A, add B where A/B share a day cap or exclusion group) races if both
      // fire together: B's check can run before A's cancellation has landed and wrongly see A as
      // still active, blocking the switch. Guest edits target already-kept slots, unaffected by
      // this race, so they still run concurrently with the cancel phase.
      const guestPromise = runGuestUpdates();
      const cancelResults = await Promise.allSettled(cancelPromises);
      const addPromises = addIds.map((slotId) =>
        registerForActivityFromSession({
          activityId: activity.id,
          timeSlotId: slotId,
          landingPageId,
          dietaryRestrictions: activity.collect_dietary_restrictions ? dietaryRestrictions : undefined,
          customFields: Object.keys(customFieldValues).length > 0 ? customFieldValues : undefined,
          phoneNumber: phoneOverride,
          countryDialCodeId: phoneOverride ? countryDialCodeId : undefined,
          suppressEmail: isMultiSlotAdd,
          guestNames: addGuestNamesPayload,
        })
      );
      const [addResults, guest] = await Promise.all([Promise.allSettled(addPromises), guestPromise]);

      // Multi-slot adds suppress per-call emails; send one consolidated confirmation.
      void sendConsolidatedEmailsForBatch(addResults, isMultiSlotAdd, activity.id);

      const succeededAdds = succeededSlotIdsFrom(addResults, addIds);
      const succeededCancels: number[] = [];
      cancelResults.forEach((r, i) => {
        if (r.status === "fulfilled" && r.value.success) succeededCancels.push(cancelIds[i]!);
      });

      const anyFailure =
        succeededAdds.length < addIds.length ||
        succeededCancels.length < cancelIds.length ||
        guest.succeeded < guest.total;

      // Always report the succeeded portion so the card + counts update. Guest edits don't change
      // which slots are held, but a guest-edit waitlist move flips a slot's seat usage, so the
      // empty added/cancelled arrays still trigger the parent's count refresh.
      onSuccess({ activityId: activity.id, addedSlotIds: succeededAdds, cancelledSlotIds: succeededCancels });

      if (!anyFailure) {
        showToast(
          pickReconcileToast({
            addedCount: succeededAdds.length,
            cancelledCount: succeededCancels.length,
            guestMovedCount: guest.moved,
            guestUpdatedCount: guest.succeeded,
          }),
          "success"
        );
        setIsReconciling(false);
        onClose();
        return;
      }

      // Partial: reflect the new reality (so the diff is consistent) and surface the failure.
      const addVerdict = tallyRegisterResults(addResults);
      const newRegistered = new Set(registeredSlotIds);
      for (const id of succeededCancels) newRegistered.delete(id);
      for (const id of succeededAdds) newRegistered.add(id);

      // Re-baseline the guests only when every attempted write landed — a mixed guest result leaves
      // the kept slots holding different lists, which a single baseline can't represent, so we leave
      // the baseline alone and tell the user to reopen (which re-seeds from a fresh read).
      const guestFullySynced = guest.total > 0 && guest.fullySynced;
      const guestPartial = guest.total > 0 && guest.succeeded > 0 && guest.succeeded < guest.total;
      const errorMessage =
        addVerdict.firstError ||
        (guestPartial
          ? "Some guest changes couldn't be saved, so your guest list may differ between time slots. Reopen to review."
          : "Some changes couldn't be saved. Please try again.");

      onPartialFailure({
        reconciledSlotIds: Array.from(newRegistered),
        errorMessage,
        guestBaseline: guestFullySynced ? guestNamesForWrite : undefined,
      });
      setIsReconciling(false);
      showToast("Some changes couldn't be saved", "error");
    },
    [
      activity,
      landingPageId,
      viewer,
      phoneDigits,
      countryDialCodeId,
      dietaryRestrictions,
      customFieldValues,
      guestCount,
      guestNames,
      registrationBySlotId,
      registeredSlotIds,
      onSuccess,
      onClose,
      showToast,
      onReconcileStart,
      onPartialFailure,
    ]
  );

  return { isReconciling, runReconcile };
}
